import { uniq } from "lodash";
import { RECORD_TYPES } from "../types";
import type {
  CommunityMember,
  ComplexValue,
  MemberTag,
  RecordType,
  SelectionAction,
  SelectionState,
  SimpleValue,
  Space,
  SpaceGroup,
  Value,
} from "../types";
import { SELECTION_ACTIONS } from "./selectionActions";

export const initialState = (
  value: Value,
  includeAllResultTypes = false,
): SelectionState => ({
  spaceGroups: [],
  spaces: [],
  memberTags: [],
  communityMembers: [],
  memberCount: 0,
  isLoading: false,
  errorMessage: null,
  value: !includeAllResultTypes
    ? [...((value as SimpleValue) || [])]
    : {
        ...((value as ComplexValue) || {}),
        members_ids: (value as ComplexValue)?.members_ids || [],
        spaces_ids: (value as ComplexValue)?.spaces_ids || [],
        space_groups_ids: (value as ComplexValue)?.space_groups_ids || [],
        member_tags_ids: (value as ComplexValue)?.member_tags_ids || [],
      },
});

// Helper functions for handling different record types
const handleAddSpaceGroup = (
  state: SelectionState,
  record: RecordType,
  shouldUpdateValue: boolean,
): SelectionState => ({
  ...state,
  spaceGroups: [...state.spaceGroups, record as SpaceGroup],
  memberCount:
    state.memberCount + (record.active_space_group_members_count || 0),
  value: shouldUpdateValue
    ? {
        ...(state.value as ComplexValue),
        space_groups_ids: uniq([
          ...(state.value as ComplexValue).space_groups_ids,
          record.id,
        ]),
      }
    : state.value,
});

const handleAddSpace = (
  state: SelectionState,
  record: RecordType,
  shouldUpdateValue: boolean,
): SelectionState => ({
  ...state,
  spaces: [...state.spaces, record as Space],
  memberCount: state.memberCount + (record.active_space_members_count || 0),
  value: shouldUpdateValue
    ? {
        ...(state.value as ComplexValue),
        spaces_ids: uniq([
          ...(state.value as ComplexValue).spaces_ids,
          record.id,
        ]),
      }
    : state.value,
});

const handleAddMemberTag = (
  state: SelectionState,
  record: RecordType,
  shouldUpdateValue: boolean,
): SelectionState => ({
  ...state,
  memberTags: [...state.memberTags, record as MemberTag],
  memberCount: state.memberCount + (record.tagged_members_count || 0),
  value: shouldUpdateValue
    ? {
        ...(state.value as ComplexValue),
        member_tags_ids: uniq([
          ...(state.value as ComplexValue).member_tags_ids,
          record.id,
        ]),
      }
    : state.value,
});

const handleAddCommunityMember = (
  state: SelectionState,
  record: RecordType,
  shouldUpdateValue: boolean,
  shouldIncludeAllResultTypes: boolean,
): SelectionState => ({
  ...state,
  communityMembers: [...state.communityMembers, record as CommunityMember],
  memberCount: state.memberCount + 1,
  value: !shouldUpdateValue
    ? state.value
    : !shouldIncludeAllResultTypes
      ? [...(state.value as SimpleValue), record.id]
      : {
          ...(state.value as ComplexValue),
          members_ids: uniq([
            ...(state.value as ComplexValue).members_ids,
            record.id,
          ]),
        },
});

const handleRemoveSpaceGroup = (
  state: SelectionState,
  record: RecordType,
): SelectionState => ({
  ...state,
  spaceGroups: state.spaceGroups.filter(
    spaceGroup => spaceGroup.id !== record.id,
  ),
  memberCount:
    state.memberCount - (record.active_space_group_members_count || 0),
  value: {
    ...(state.value as ComplexValue),
    space_groups_ids: uniq(
      (state.value as ComplexValue).space_groups_ids.filter(
        spaceGroupId => spaceGroupId !== record.id,
      ),
    ),
  },
});

const handleRemoveSpace = (
  state: SelectionState,
  record: RecordType,
): SelectionState => ({
  ...state,
  spaces: state.spaces.filter(space => space.id !== record.id),
  memberCount: state.memberCount - (record.active_space_members_count || 0),
  value: {
    ...(state.value as ComplexValue),
    spaces_ids: uniq(
      (state.value as ComplexValue).spaces_ids.filter(
        spaceId => spaceId !== record.id,
      ),
    ),
  },
});

const handleRemoveMemberTag = (
  state: SelectionState,
  record: RecordType,
): SelectionState => ({
  ...state,
  memberTags: state.memberTags.filter(memberTag => memberTag.id !== record.id),
  memberCount: state.memberCount - (record.tagged_members_count || 0),
  value: {
    ...(state.value as ComplexValue),
    member_tags_ids: uniq(
      (state.value as ComplexValue).member_tags_ids.filter(
        memberTagId => memberTagId !== record.id,
      ),
    ),
  },
});

const handleRemoveCommunityMember = (
  state: SelectionState,
  record: RecordType,
  shouldIncludeAllResultTypes: boolean,
): SelectionState => ({
  ...state,
  communityMembers: state.communityMembers.filter(
    communityMember => communityMember.id !== record.id,
  ),
  memberCount: state.memberCount - 1,
  value: !shouldIncludeAllResultTypes
    ? uniq(
        (state.value as SimpleValue).filter(
          communityMemberId => communityMemberId !== record.id,
        ),
      )
    : {
        ...(state.value as ComplexValue),
        members_ids: uniq(
          (state.value as ComplexValue).members_ids.filter(
            memberId => memberId !== record.id,
          ),
        ),
      },
});

// Record type handlers lookup
const addHandlers = {
  [RECORD_TYPES.SPACE_GROUP]: handleAddSpaceGroup,
  [RECORD_TYPES.SPACE]: handleAddSpace,
  [RECORD_TYPES.MEMBER_TAG]: handleAddMemberTag,
  [RECORD_TYPES.COMMUNITY_MEMBER]: handleAddCommunityMember,
} as const;

const removeHandlers = {
  [RECORD_TYPES.SPACE_GROUP]: handleRemoveSpaceGroup,
  [RECORD_TYPES.SPACE]: handleRemoveSpace,
  [RECORD_TYPES.MEMBER_TAG]: handleRemoveMemberTag,
  [RECORD_TYPES.COMMUNITY_MEMBER]: handleRemoveCommunityMember,
} as const;

// Main reducer function
export const selectionReducer = (
  state: SelectionState,
  action: SelectionAction,
): SelectionState => {
  const { type, payload } = action;

  switch (type) {
    case SELECTION_ACTIONS.ADD: {
      const {
        record,
        includeAllResultTypes: shouldIncludeAllResultTypes,
        shouldUpdateValue,
      } = payload;
      const { type: recordType } = record || {};
      const handler = addHandlers[recordType] || handleAddCommunityMember;
      return handler(
        state,
        record,
        shouldUpdateValue,
        shouldIncludeAllResultTypes,
      );
    }

    case SELECTION_ACTIONS.REMOVE: {
      const { record, includeAllResultTypes: shouldIncludeAllResultTypes } =
        payload;
      const { type: recordType } = record || {};
      const handler = removeHandlers[recordType] || handleRemoveCommunityMember;
      return handler(state, record, shouldIncludeAllResultTypes);
    }

    case SELECTION_ACTIONS.INITIAL_FETCH_START:
      return {
        ...state,
        isLoading: true,
      };

    case SELECTION_ACTIONS.INITIAL_FETCH_SUCCESS:
      return {
        ...state,
        isLoading: false,
      };

    case SELECTION_ACTIONS.INITIAL_FETCH_FAILURE:
      return {
        ...state,
        isLoading: false,
        errorMessage: payload.errorMessage,
      };

    case SELECTION_ACTIONS.RESET:
      return initialState(null, !!payload.includeAllResultTypes);

    default:
      return state;
  }
};
